import { useState, useEffect } from 'react'
import validate from 'validate.js'
import useRouter from 'utils/useRouter'
import { useSessionService } from 'hooks/useSessionService'
import { useUsuarioService } from 'hooks/useUsuarioService'

const schema = {
  cpf: {
    format: {
      pattern: '([0-9]{2}[.]?[0-9]{3}[.]?[0-9]{3}[/]?[0-9]{4}[-]?[0-9]{2})|([0-9]{3}[.]?[0-9]{3}[.]?[0-9]{3}[-]?[0-9]{2})',
      message: '^Digite um CPF válido',
    },
    presence: { allowEmpty: false, message: '^CPF é obrigatório' },
  },
}

/**
 * @description Formata o Input CPF com máscara
 * @param {string} value valor do campo CPF
 */
const cpfMask = (value) =>
  value
    .replace(/\D/g, '') // substitui qualquer caracter que nao seja numero por nada
    .replace(/(\d{3})(\d)/, '$1.$2') // captura 2 grupos de numero o primeiro de 3 e o segundo de 1, apos capturar o primeiro grupo ele adiciona um ponto antes do segundo grupo de numero
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1') // captura 2 numeros seguidos de um traço e não deixa ser digitado mais nada

/**
 * @description Valida o número de CPF (Algoritmo idêntico ao original)
 * @param {string} number String com o número do CPF
 * @returns {boolean} Caso o CPF seja um número válido
 */
const validateCPF = (number) => {
  if (!number) return false
  const cpf = number.replace(/[/.-]/g, '') //! Limpar os possiveis caracteres ".", "-" e " " que venham junto aos números do CPF

  let Soma = 0
  let Resto

  for (let i = 1; i <= 9; i += 1) {
    Soma += parseInt(cpf.substring(i - 1, i), 10) * (11 - i)
  }

  Resto = (Soma * 10) % 11

  if (Resto === 10 || Resto === 11) {
    Resto = 0
  }

  if (Resto !== parseInt(cpf.substring(9, 10), 10)) {
    return false
  }

  Soma = 0

  for (let j = 1; j <= 10; j += 1) {
    Soma += parseInt(cpf.substring(j - 1, j), 10) * (12 - j)
  }

  Resto = (Soma * 10) % 11

  if (Resto === 10 || Resto === 11) {
    Resto = 0
  }

  if (Resto !== parseInt(cpf.substring(10, 11), 10)) {
    return false
  }

  return true
}

export const useEsqueceuSenhaForm = () => {
  const sessionService = useSessionService()
  const usuarioService = useUsuarioService()
  const router = useRouter()

  const [isLoading, setIsLoading] = useState(false)
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
    cpfValido: false,
  })

  useEffect(() => {
    const errors = validate(formState.values, schema)

    setFormState((prevState) => ({
      ...prevState,
      isValid: !errors,
      errors: errors || {},
    }))
  }, [formState.values])

  const handleChange = (event) => {
    event.persist()
    const { name, value, type, checked } = event.target
    const fieldValue = type === 'checkbox' ? checked : value

    if (name === 'cpf') {
      const maskedValue = cpfMask(fieldValue)
      const isCpfValido = validateCPF(maskedValue)

      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [name]: maskedValue,
        },
        touched: {
          ...prevState.touched,
          [name]: true,
        },
        cpfValido: isCpfValido,
      }))
    } else {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [name]: fieldValue,
        },
        touched: {
          ...prevState.touched,
          [name]: true,
        },
      }))
    }
  }

  const handleSubmit = async (event) => {
    if (event) event.preventDefault()

    if (!formState.isValid || !formState.cpfValido || isLoading) return

    setIsLoading(true)
    try {
      const dadosRegistrados = await usuarioService.verificarUsuarioComDadosRegistrados(formState.values.cpf)
      if (dadosRegistrados) {
        await sessionService.solicitarRedefinirSenha(formState)
        router.history.push(`/auth/login`)
        setFormState({
          isValid: false,
          values: {},
          touched: {},
          errors: {},
          cpfValido: false,
        })
      }
    } catch (err) {
      console.error('Erro ao solicitar redefinição de senha:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const hasError = (field) => {
    if (field === 'cpf') {
      return formState.touched.cpf && (!!formState.errors.cpf || !formState.cpfValido)
    }
    return formState.touched[field] && !!formState.errors[field]
  }

  return {
    formState,
    isLoading,
    handleChange,
    handleSubmit,
    hasError,
  }
}
